/**
 * ============================================================================
 * OFFLINE CONTEXT v1.0.0 - PWA OFFLINE STATE MANAGEMENT
 * ============================================================================
 *
 * React Context für Offline-State-Management:
 * - Online/Offline Status Tracking
 * - Sync-Queue Verwaltung
 * - Automatischer Sync bei Reconnection
 * - Service Worker Kommunikation
 *
 * @version 1.0.0
 * @date 2026-01-16
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { indexedDBService } from '../services/indexedDB.service';
import { useToken } from './TokenContext';

console.log('📶 OfflineContext.js v1.0.0 loaded');

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const OfflineContext = createContext(null);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function OfflineProvider({ children }) {
  const { token } = useToken();

  // State
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // Refs
  const syncTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);

  // ============================================================================
  // ONLINE/OFFLINE EVENT HANDLERS
  // ============================================================================

  const handleOnline = useCallback(() => {
    console.log('📶 Network: Online');
    setIsOnline(true);
    setSyncError(null);

    // Verzögerter Sync (warte bis Verbindung stabil)
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      triggerSync();
    }, 2000);
  }, []);

  const handleOffline = useCallback(() => {
    console.log('📶 Network: Offline');
    setIsOnline(false);

    // Sync-Timeout abbrechen
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
  }, []);

  // ============================================================================
  // SYNC QUEUE MANAGEMENT
  // ============================================================================

  /**
   * Aktualisiert die Anzahl ausstehender Sync-Operationen
   */
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await indexedDBService.getSyncQueueCount();
      setPendingSyncCount(count);
      return count;
    } catch (error) {
      console.error('❌ Error updating pending count:', error);
      return 0;
    }
  }, []);

  /**
   * Synchronisiert alle ausstehenden Operationen mit dem Server
   */
  const triggerSync = useCallback(async () => {
    if (isSyncing || !isOnline) {
      console.log('⏸️ Sync skipped:', isSyncing ? 'already syncing' : 'offline');
      return { success: false, reason: isSyncing ? 'already_syncing' : 'offline' };
    }

    if (!token) {
      console.log('⏸️ Sync skipped: no token');
      return { success: false, reason: 'no_token' };
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      console.log('🔄 Starting sync...');

      const queue = await indexedDBService.getSyncQueue();
      console.log(`📦 Sync queue has ${queue.length} items`);

      if (queue.length === 0) {
        console.log('✅ No items to sync');
        await indexedDBService.setLastSyncTime();
        const lastSync = await indexedDBService.getLastSyncTime();
        setLastSyncTime(lastSync);
        setIsSyncing(false);
        return { success: true, synced: 0 };
      }

      let synced = 0;
      let failed = 0;

      for (const item of queue) {
        try {
          console.log(`🔄 Syncing: ${item.type} ${item.operation} - ${item.endpoint}`);

          const response = await fetch(item.endpoint, {
            method: item.method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: item.payload ? JSON.stringify(item.payload) : undefined
          });

          if (response.ok) {
            await indexedDBService.markSyncItemProcessed(item.id);
            synced++;
            console.log(`✅ Synced: ${item.type} ${item.operation}`);
          } else {
            const errorText = await response.text();
            console.warn(`⚠️ Sync failed: ${response.status} - ${errorText}`);
            await indexedDBService.markSyncItemFailed(item.id, errorText);
            failed++;
          }
        } catch (itemError) {
          console.error(`❌ Sync error for item:`, itemError);
          await indexedDBService.markSyncItemFailed(item.id, itemError.message);
          failed++;
        }
      }

      // Update Status
      await indexedDBService.setLastSyncTime();
      const lastSync = await indexedDBService.getLastSyncTime();
      setLastSyncTime(lastSync);
      await updatePendingCount();

      console.log(`✅ Sync complete: ${synced} synced, ${failed} failed`);

      if (failed > 0) {
        setSyncError(`${failed} Änderungen konnten nicht synchronisiert werden`);
      }

      return { success: true, synced, failed };

    } catch (error) {
      console.error('❌ Sync error:', error);
      setSyncError('Synchronisierung fehlgeschlagen');
      return { success: false, error: error.message };

    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, isOnline, token, updatePendingCount]);

  /**
   * Fügt eine Operation zur Sync-Queue hinzu
   */
  const addToQueue = useCallback(async (operation) => {
    const success = await indexedDBService.addToSyncQueue(operation);
    if (success) {
      await updatePendingCount();
    }
    return success;
  }, [updatePendingCount]);

  // ============================================================================
  // SERVICE WORKER COMMUNICATION
  // ============================================================================

  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      const { type } = event.data || {};

      switch (type) {
        case 'SYNC_TRIGGER':
          console.log('📶 Received SYNC_TRIGGER from Service Worker');
          triggerSync();
          break;

        case 'SYNC_STARTED':
          console.log('📶 Service Worker started sync');
          break;

        default:
          break;
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [triggerSync]);

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  useEffect(() => {
    const initialize = async () => {
      if (isInitializedRef.current) return;
      isInitializedRef.current = true;

      console.log('📶 Initializing OfflineContext...');

      // Event Listener registrieren
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      // Initial Status laden
      const lastSync = await indexedDBService.getLastSyncTime();
      setLastSyncTime(lastSync);

      await updatePendingCount();

      // Background Sync registrieren (falls verfügbar)
      if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
        try {
          const registration = await navigator.serviceWorker.ready;
          await registration.sync.register('planivio-sync');
          console.log('✅ Background Sync registered');
        } catch (error) {
          console.log('ℹ️ Background Sync not available:', error.message);
        }
      }

      console.log('✅ OfflineContext initialized');
    };

    initialize();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [handleOnline, handleOffline, updatePendingCount]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const contextValue = {
    // Status
    isOnline,
    pendingSyncCount,
    lastSyncTime,
    isSyncing,
    syncError,

    // Actions
    triggerSync,
    addToQueue,
    updatePendingCount,

    // Utilities
    formatLastSyncTime: () => {
      if (!lastSyncTime) return 'Noch nie synchronisiert';
      const date = new Date(lastSyncTime);
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  return (
    <OfflineContext.Provider value={contextValue}>
      {children}
    </OfflineContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook zum Zugriff auf den Offline-Context
 * @returns {Object} Offline-Context Werte und Funktionen
 */
export function useOffline() {
  const context = useContext(OfflineContext);

  if (!context) {
    // Fallback für Komponenten außerhalb des Providers
    console.warn('⚠️ useOffline called outside of OfflineProvider');
    return {
      isOnline: navigator.onLine,
      pendingSyncCount: 0,
      lastSyncTime: null,
      isSyncing: false,
      syncError: null,
      triggerSync: async () => ({ success: false, reason: 'no_provider' }),
      addToQueue: async () => false,
      updatePendingCount: async () => 0,
      formatLastSyncTime: () => ''
    };
  }

  return context;
}

export default OfflineContext;
